import type React from 'react';
import { useTranslation } from 'react-i18next';
import { formatGel } from '@/utils/money';
import { MetricRow, OverviewCard, ThresholdBar } from '../overviewCard';
import { formatSignedGel } from '../overviewMeta';
import {
    BREAK_EVEN as META,
    breakEvenSegments,
    resolveBreakEvenDelta,
    resolveBreakEvenLabel,
} from './breakEvenMeta';
import type { BreakEvenData } from './useBreakEven';

type BreakEvenViewProps = {
    data: BreakEvenData;
};

/**
 * Break-even — render only.
 *
 * The bar is the month, not a percentage: red until costs were covered, green
 * after. It answers "is the company profitable" with *how much of the month it
 * took*, which a margin cannot say.
 */
export const BreakEvenView: React.FC<BreakEvenViewProps> = ({ data }) => {
    const { t } = useTranslation('pages');
    const delta = resolveBreakEvenDelta(data.deltaDays);
    const reached = resolveBreakEvenLabel(data.breakEvenDay);

    return (
        <OverviewCard
            delta={
                delta === null
                    ? null
                    : {
                          label: t(delta.key, delta.values ?? {}),
                          tone: data.tone,
                      }
            }
            icon={META.icon}
            question={t(META.keys.question)}
            slot={META.slot}
            title={t(META.keys.title)}
            tone={data.tone}
            verdict={{
                icon:
                    data.resultTetri < 0
                        ? 'solar:close-circle-bold'
                        : 'solar:check-circle-bold',
                label: t(
                    data.resultTetri < 0
                        ? META.keys.unprofitable
                        : META.keys.profitable,
                ),
                tone: data.tone,
                value: formatSignedGel(data.resultTetri),
            }}
        >
            <MetricRow
                label={t(META.keys.revenue)}
                slot={`${META.slot}-revenue`}
                value={formatGel(data.revenueTetri)}
            />
            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-3 text-xs">
                    <span className="font-medium text-foreground">
                        {t(reached.key, reached.values ?? {})}
                    </span>
                    <span className="text-muted">
                        {t(META.keys.profitZone)}
                    </span>
                </div>
                <ThresholdBar
                    ariaLabel={t(META.keys.scaleAria)}
                    marker={null}
                    segments={breakEvenSegments(
                        data.breakEvenDay,
                        data.daysInMonth,
                    )}
                    slot={`${META.slot}-scale`}
                    ticks={[
                        t(META.keys.firstDay),
                        t(META.keys.lastDay, { day: data.daysInMonth }),
                    ]}
                />
            </div>
        </OverviewCard>
    );
};
